import { useMemo, useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { BookmarkAdd, MoreVert } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useTrackingPresets } from '../../hooks/useTrackingPresets';
import {
  applyPreset, isPresetActive,
  type PresetApplyTarget, type PresetMatchState, type TrackingPreset,
} from '../../utils/tracking-presets';
import { ReportState } from './useReportState';
import SavePresetDialog from './SavePresetDialog';
import PresetManagerDialog from './PresetManagerDialog';

interface Props {
  report: ReportState;
  vLabel: (field: string) => string;
}

export default function PresetBar({ report, vLabel }: Props) {
  const { presets, savePresets } = useTrackingPresets();
  const { enqueueSnackbar } = useSnackbar();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const target: PresetApplyTarget = {
    setDateRange: report.setDateRange,
    setGroupBy: report.setGroupBy,
    setDrilldownChain: report.setDrilldownChain,
    setFilters: report.setFilters,
    setTagIds: report.setTagIds,
    setSortModel: report.setSortModel,
  };
  const matchState: PresetMatchState = useMemo(() => ({
    dateRange: report.dateRange,
    groupBy: report.groupBy,
    drilldownChain: report.drilldownChain,
    filters: report.filters,
    tagIds: report.tagIds,
    sortBy: report.sortBy ?? 'name',
    sortOrder: report.sortOrder === 'asc' ? 'asc' : 'desc',
  }), [report.dateRange, report.groupBy, report.drilldownChain, report.filters,
    report.tagIds, report.sortBy, report.sortOrder]);

  const persist = async (next: TrackingPreset[]) => {
    try {
      await savePresets(next);
    } catch {
      enqueueSnackbar('Could not save presets', { variant: 'error' });
    }
  };

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', columnGap: 0.5, flex: '1 1 auto', minWidth: 0,
        overflowX: 'auto', py: 0.25,
        scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: 5 },
      }}>
        {presets.map((p) => (
          <Button
            key={p.id}
            size="small"
            variant={isPresetActive(p, matchState) ? 'contained' : 'outlined'}
            onClick={() => applyPreset(p, target)}
            sx={{ textTransform: 'none', fontSize: '0.8rem', px: { xs: 0.75, md: 1.5 },
              minWidth: 0, flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {p.name}
          </Button>
        ))}
        <Tooltip title="Presets">
          <IconButton size="small" aria-label="Presets" sx={{ flexShrink: 0 }}
            onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); setSaveOpen(true); }}>
          <BookmarkAdd fontSize="small" sx={{ mr: 1 }} /> Save current view…
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setManageOpen(true); }}>
          Manage presets…
        </MenuItem>
      </Menu>

      <SavePresetDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        state={matchState}
        vLabel={vLabel}
        onSave={(p) => persist([...presets, p])}
      />
      <PresetManagerDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        presets={presets}
        state={matchState}
        vLabel={vLabel}
        onApply={persist}
      />
    </>
  );
}
